import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import {
  Button as MuiButton,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Radio,
  Select as MuiSelect,
  TextField,
  Typography,
} from '@material-ui/core';
import { FiPlus, FiUpload } from 'react-icons/fi';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';

const SAO_PAULO_TIMEZONE = 'America/Sao_Paulo';

export interface NotificationForm {
  title: string;
  description: string;
  notificationType: 'default' | 'link';
  url: string;
  tela: string;
  livro: string;
  capitulo: string;
  versiculo: string;
  canal: string;
  camera: string;
  id: string;
  image_url: string;
  scheduled: boolean;
  scheduledDate: string;
}

export interface NotificationCreateProps {
  open?: boolean;
  onClose?: () => void;
  onCreated?: () => void | Promise<void>;
  initialValues?: Partial<NotificationForm>;
}

const createInitialForm = (initialValues?: Partial<NotificationForm>): NotificationForm => ({
  title: '',
  description: '',
  notificationType: 'default',
  url: '',
  tela: '',
  livro: '',
  capitulo: '',
  versiculo: '',
  canal: '',
  camera: '',
  id: '',
  image_url: '',
  scheduled: false,
  scheduledDate: '',
  ...initialValues,
});

const screenOptions: Array<[string, string]> = [
  ['home', 'Home'],
  ['biblia_busca', 'Busca da Bíblia'],
  ['biblia_leitura', 'Leitura da Bíblia'],
  ['radio_aparecida', 'Rádio Aparecida'],
  ['radio_pop', 'Rádio Pop'],
  ['tv_aparecida', 'TV Aparecida'],
  ['deus_conosco_dia_dia', 'Deus Conosco — Dia a dia'],
  ['deus_conosco_meditacao_diaria', 'Deus Conosco — Meditação'],
  ['deus_conosco_santo_dia', 'Deus Conosco — Santo do dia'],
  ['aparecida_play_canais', 'Aparecida Play — Canais'],
  ['aparecida_play_playlists', 'Aparecida Play — Playlists'],
  ['aparecida_play_video', 'Aparecida Play — Vídeo'],
  ['familia_home', 'Família — Home'],
  ['familia_cadastro', 'Família — Cadastro'],
  ['familia_revista', 'Família — Revista'],
  ['aparecida_ao_vivo_camera', 'Aparecida ao vivo — Câmera'],
  ['noticia', 'Notícia'],
  ['home_noticia', 'Notícias — Home'],
];

const channelOptions: Array<[string, string]> = [
  ['1', 'Aparecida Ao Vivo'],
  ['2', 'A Rádio Pop'],
  ['3', 'Devotos Mirins'],
  ['4', 'A12'],
  ['5', 'Rádio Aparecida'],
  ['6', 'TV Aparecida'],
  ['7', 'Jovens de Maria'],
  ['8', 'Redentoristas'],
  ['9', 'Santuário Nacional'],
];

const cameraOptions: Array<[string, string]> = [
  ['1', 'Aparecida Ao Vivo'],
  ['2', 'Nicho de N. Senhora'],
  ['3', 'Imagem de N. Senhora'],
  ['4', 'Capela São José'],
  ['5', 'Capela Santíssimo'],
  ['7', 'Altar Central'],
  ['8', 'Santuário Nacional'],
  ['10', 'Cidade Aparecida'],
  ['11', 'Altar Bas. Histórica'],
  ['12', 'Sala das Promessas'],
  ['14', 'Basílica Histórica'],
];

const toSaoPauloDate = (value: string): string | null => {
  if (!value) return null;
  return `${value}:00-03:00`;
};

const CreateNotification: React.FC<NotificationCreateProps> = ({
  open = true,
  onClose = () => undefined,
  onCreated = () => undefined,
  initialValues,
}) => {
  const { addToast } = useToast();
  const [form, setForm] = useState<NotificationForm>(() => createInitialForm(initialValues));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(initialValues?.image_url || null);

  useEffect(() => {
    if (!open) return;

    setForm(createInitialForm(initialValues));
    setImageFile(null);
    setImagePreview(initialValues?.image_url || null);
  }, [initialValues, open]);

  const updateForm = (field: keyof NotificationForm, value: any) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setForm(createInitialForm());
    setImageFile(null);
    setImagePreview(null);
  };

  const [scheduledDateValue = '', scheduledTimeValue = ''] = form.scheduledDate.split('T');

  const updateScheduledDate = (value: string) => {
    updateForm('scheduledDate', value ? `${value}T${scheduledTimeValue}` : '');
  };

  const updateScheduledTime = (value: string) => {
    updateForm('scheduledDate', `${scheduledDateValue}T${value}`);
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast({ type: 'error', title: 'Selecione um arquivo de imagem válido' });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return form.image_url || null;
    const body = new FormData();
    body.append('image', imageFile);
    setUploading(true);
    try {
      const response = await api.post('/notifications/assets', body);
      return response.data.url;
    } finally {
      setUploading(false);
    }
  };

  const buildData = () => {
    const data: Record<string, any> = {};
    if (form.tela) data.tela = form.tela;

    if (form.notificationType === 'link' && form.url) {
      data.type = 'LINK';
      data.url = form.url;
    }

    switch (form.tela) {
      case 'biblia_leitura':
        if (form.livro) data.livro = form.livro;
        if (form.capitulo) data.capitulo = form.capitulo;
        if (form.versiculo) data.versiculo = form.versiculo;
        break;
      case 'aparecida_play_playlists':
        if (form.canal) data.canal = form.canal;
        break;
      case 'aparecida_play_video':
      case 'noticia':
        if (form.id) data.id = form.id;
        break;
      case 'aparecida_ao_vivo_camera':
        if (form.camera) data.camera = form.camera;
        break;
      default:
        break;
    }

    return data;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      addToast({ type: 'error', title: 'Título e mensagem são obrigatórios' });
      return;
    }
    if (form.scheduled && (!scheduledDateValue || !scheduledTimeValue)) {
      addToast({ type: 'error', title: 'Informe a data e hora do agendamento' });
      return;
    }

    setSaving(true);
    try {
      const imageUrl = await uploadImage();
      await api.post('/notifications', {
        title: form.title.trim(),
        notification_text: form.description.trim(),
        data: buildData(),
        scheduled: form.scheduled,
        scheduled_date: form.scheduled ? toSaoPauloDate(form.scheduledDate) : null,
        timezone: SAO_PAULO_TIMEZONE,
        image_url: imageUrl,
      });
      addToast({ type: 'success', title: 'Notificação colocada na fila de envio' });
      resetForm();
      handleClose();
      await onCreated();
    } catch (error) {
      const responseError =
        error && typeof error === 'object' && 'response' in error
          ? (error as {
              response?: { data?: { message?: string } };
            }).response
          : undefined;

      addToast({
        type: 'error',
        title: 'Erro ao cadastrar notificação',
        description: responseError?.data?.message || 'Tente novamente mais tarde.',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderParameterField = () => {
    if (form.tela === 'biblia_leitura') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Livro" value={form.livro} onChange={event => updateForm('livro', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Capítulo" value={form.capitulo} onChange={event => updateForm('capitulo', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Versículo" value={form.versiculo} onChange={event => updateForm('versiculo', event.target.value)} />
          </Grid>
        </Grid>
      );
    }

    if (form.tela === 'aparecida_play_playlists') {
      return (
        <FormControl fullWidth>
          <InputLabel>Canal</InputLabel>
          <MuiSelect value={form.canal} onChange={event => updateForm('canal', event.target.value)}>
            {channelOptions.map(([value, label]) => <MenuItem key={value} value={value}>{value} — {label}</MenuItem>)}
          </MuiSelect>
        </FormControl>
      );
    }

    if (form.tela === 'aparecida_ao_vivo_camera') {
      return (
        <FormControl fullWidth>
          <InputLabel>Câmera</InputLabel>
          <MuiSelect value={form.camera} onChange={event => updateForm('camera', event.target.value)}>
            {cameraOptions.map(([value, label]) => <MenuItem key={value} value={label}>{label}</MenuItem>)}
          </MuiSelect>
        </FormControl>
      );
    }

    if (form.tela === 'aparecida_play_video' || form.tela === 'noticia') {
      return <TextField fullWidth label={form.tela === 'noticia' ? 'ID da notícia' : 'ID do vídeo'} value={form.id} onChange={event => updateForm('id', event.target.value)} />;
    }

    return null;
  };

  const parameterField = renderParameterField();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="notification-create-modal-title"
      aria-describedby="notification-create-modal-description"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          padding: 24,
          boxSizing: 'border-box',
          outline: 'none',
        }}
      >
        <Paper style={{ width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <Typography id="notification-create-modal-title" variant="h5" gutterBottom>
                Notificação
              </Typography>
            </div>
            <MuiButton type="button" onClick={handleClose} disabled={saving}>
              Fechar
            </MuiButton>
          </div>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth required label="Título" inputProps={{ maxLength: 100 }} value={form.title} onChange={event => updateForm('title', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth required multiline minRows={1} label="Descrição / mensagem" inputProps={{ maxLength: 300 }} value={form.description} onChange={event => updateForm('description', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <MuiButton component="label" variant="outlined" startIcon={<FiUpload />} disabled={uploading}>
                    {imageFile ? imageFile.name : 'Enviar imagem'}
                    <input hidden type="file" accept="image/*" onChange={handleImageChange} />
                  </MuiButton>
                  {imagePreview && <img src={imagePreview} alt="Prévia da miniatura" style={{ display: 'block', maxWidth: 180, maxHeight: 90 }} />}
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <MuiSelect value={form.notificationType} onChange={event => updateForm('notificationType', event.target.value)}>
                    <MenuItem value="default">Somente texto</MenuItem>
                    <MenuItem value="link">Texto com link</MenuItem>
                  </MuiSelect>
                </FormControl>
              </Grid>
              {form.notificationType === 'link' && (
                <Grid item xs={12}>
                  <TextField fullWidth label="URL para abrir" value={form.url} onChange={event => updateForm('url', event.target.value)} />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Direcionamento de tela</InputLabel>
                  <MuiSelect value={form.tela} onChange={event => updateForm('tela', event.target.value)}>
                    <MenuItem value=""><em>Nenhuma</em></MenuItem>
                    {screenOptions.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                  </MuiSelect>
                </FormControl>
              </Grid>
              {parameterField && <Grid item xs={12}>{parameterField}</Grid>}
              <Grid item xs={12}>
                <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                  <Radio
                    checked={form.scheduled}
                    onClick={() => updateForm('scheduled', !form.scheduled)}
                    color="primary"
                    inputProps={{ 'aria-label': 'Agendar' }}
                  />
                  Agendar
                </label>
              </Grid>
              {form.scheduled && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required type="date" label="Data" InputLabelProps={{ shrink: true }} inputProps={{ max: '9999-12-31' }} value={scheduledDateValue} onChange={event => updateScheduledDate(event.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required type="time" label="Hora" InputLabelProps={{ shrink: true }} value={scheduledTimeValue} onChange={event => updateScheduledTime(event.target.value)} />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <MuiButton type="button" onClick={resetForm} disabled={saving || uploading}>Limpar</MuiButton>
                  <MuiButton type="button" onClick={handleClose} disabled={saving || uploading}>Cancelar</MuiButton>
                  <MuiButton type="submit" variant="contained" color="primary" disabled={saving || uploading} startIcon={saving ? <CircularProgress size={16} /> : <FiPlus />}>
                    {saving ? (form.scheduled ? 'Agendando...' : 'Enviando...') : (form.scheduled ? 'Agendar' : 'Enviar')}
                  </MuiButton>
                </div>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </div>
    </Modal>
  );
};

export default CreateNotification;
